import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AIActionCallDto } from '@/Domain/api/Action/dto/Action.dto';
import { Badge, BadgeVariantProps } from '@/Domain/components/Shared/Badge/Badge';
import { formatRelativeTime } from '@/Domain/helper/RelativeTime.helper';
import { SCENARIO_ID } from '@/context';

type Severity = 'success' | 'warning' | 'error';

const getSeverity = (call: AIActionCallDto): Severity => {
  const code = call.http_status_code;
  if (code == null) return call.status === 'error' ? 'error' : 'success';
  if (code >= 500) return 'error';
  if (code >= 400) return 'warning';
  return 'success';
};

const SEVERITY_VARIANT: Record<Severity, BadgeVariantProps['variant']> = {
  success: 'success',
  warning: 'warning',
  error: 'error',
};

const formatValue = (value: unknown): string =>
  typeof value === 'string' ? value : JSON.stringify(value);

export const ActionLogRow = ({ call }: { call: AIActionCallDto }) => {
  const { t } = useTranslation('actions_page', { keyPrefix: 'logs' });

  const severity = getSeverity(call);
  const codeLabel =
    call.http_status_code != null ? String(call.http_status_code) : t('no_response');
  const params = Object.entries(call.tool_input);
  const conversationHref = `/i/${SCENARIO_ID}/conversation/${call.conversation_id}`;

  return (
    <div className="space-y-3 rounded-xl border border-border p-4" data-testid="action-log-row">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant={SEVERITY_VARIANT[severity]} size="lg" data-testid="action-log-status">
            {codeLabel}
          </Badge>
          <span className="text-sm font-medium text-foreground">{call.tool_name}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 text-xs text-foreground-subtle">
          <span>{formatRelativeTime(call.created_at)}</span>
          {call.duration_ms != null && <span>{`· ${call.duration_ms}ms`}</span>}
        </div>
      </div>

      {call.request_url && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">
            {t('request_url_label')}
          </p>
          <p
            className="mt-1 break-all font-mono text-sm text-foreground"
            data-testid="action-log-url"
          >
            {call.request_url}
          </p>
        </div>
      )}

      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">
          {t('params_label')}
        </p>
        {params.length > 0 ? (
          <div className="mt-1 space-y-1">
            {params.map(([key, value]) => (
              <div key={key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-2 text-sm">
                <span className="truncate font-mono text-muted-foreground">{key}</span>
                <span className="break-words font-mono text-foreground">{formatValue(value)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-sm text-foreground-subtle">{t('no_params')}</p>
        )}
      </div>

      {call.error_message ? (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-destructive">
            {t('error_label')}
          </p>
          <p className="mt-1 break-words text-sm text-destructive">{call.error_message}</p>
        </div>
      ) : call.tool_output_preview ? (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">
            {t('response_label')}
          </p>
          <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-muted p-3 text-xs text-foreground">
            {call.tool_output_preview}
          </pre>
        </div>
      ) : null}

      <a
        href={conversationHref}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        data-testid="action-log-conversation-link"
      >
        <ExternalLink className="size-3.5" />
        {t('view_conversation')}
      </a>
    </div>
  );
};
